import React, { useContext, useEffect } from 'react'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import CookiePreferences from '../../components/CookiePreferences/CookiePreferences'
import { CONTACT } from '../../config/site'
import './privacy.scss'

/**
 * Privacy and cookie policy. Written for the site as it actually is: a
 * Formspree contact form, a Calendly embed, cookieless Vercel
 * analytics, and a few localStorage keys of our own. If any of those
 * change, the tables below must change with them.
 */
const Privacy = () => {
  const { translations: t } = useContext(LanguageContext)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const ownStorage = [
    { key: 'mlmind:lang', purpose: t.privRowLang, life: t.privLifeSession },
    { key: 'mlmind:consent', purpose: t.privRowConsent, life: t.privLifeUntilCleared },
    { key: 'mlmind:estimate', purpose: t.privRowEstimate, life: t.privLifeUntilCleared },
    { key: 'mlmind:bar-dismissed, mlmind:exit-seen', purpose: t.privRowPrompts, life: t.privLifeSession },
  ]

  const thirdParty = [
    { name: 'Calendly', purpose: t.privRowCalendly, life: t.privLifeCalendly, consent: true },
    { name: 'Vercel Web Analytics', purpose: t.privRowVercel, life: t.privLifeNone, consent: true },
    { name: 'Formspree', purpose: t.privRowFormspree, life: t.privLifeNone, consent: false },
  ]

  return (
    <Layout>
      <Seo
        title={t.privSeoTitle}
        description={t.privSeoDescription}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Privacy & cookie policy', path: '/privacy' },
          ]),
        ]}
      />

      <div className='priv-page'>
        <div className='priv-inner'>
          <p className='priv-kicker'>{t.privKicker}</p>
          <h1 className='priv-title'>{t.privTitle}</h1>
          <p className='priv-updated'>{t.privUpdated}</p>
          <p className='priv-lead'>{t.privLead}</p>

          <section className='priv-section'>
            <h2>{t.privWhoTitle}</h2>
            <p>{t.privWhoText} <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.</p>
          </section>

          <section className='priv-section'>
            <h2>{t.privCollectTitle}</h2>
            <p>{t.privCollectIntro}</p>
            <ul>
              <li><strong>{t.privCollect1Title}</strong> {t.privCollect1Text}</li>
              <li><strong>{t.privCollect2Title}</strong> {t.privCollect2Text}</li>
              <li><strong>{t.privCollect4Title}</strong> {t.privCollect4Text}</li>
              <li><strong>{t.privCollect5Title}</strong> {t.privCollect5Text}</li>
            </ul>
          </section>

          <section className='priv-section'>
            <h2>{t.privCookiesTitle}</h2>
            <p>{t.privCookiesIntro}</p>

            <h3>{t.privOwnTitle}</h3>
            <p>{t.privOwnText}</p>
            <div className='priv-table-wrap'>
              <table className='priv-table'>
                <thead>
                  <tr><th>{t.privColName}</th><th>{t.privColPurpose}</th><th>{t.privColLife}</th></tr>
                </thead>
                <tbody>
                  {ownStorage.map((row) => (
                    <tr key={row.key}><td><code>{row.key}</code></td><td>{row.purpose}</td><td>{row.life}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3>{t.privThirdTitle}</h3>
            <p>{t.privThirdText}</p>
            <div className='priv-table-wrap'>
              <table className='priv-table'>
                <thead>
                  <tr><th>{t.privColService}</th><th>{t.privColPurpose}</th><th>{t.privColLife}</th><th>{t.privColConsent}</th></tr>
                </thead>
                <tbody>
                  {thirdParty.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td>{row.purpose}</td>
                      <td>{row.life}</td>
                      <td>{row.consent ? t.privYes : t.privNo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className='priv-section' id='cookie-settings'>
            <h2>{t.privManageTitle}</h2>
            <p>{t.privManageText}</p>
            <CookiePreferences />
          </section>

          <section className='priv-section'>
            <h2>{t.privControlTitle}</h2>
            <p>{t.privControlText}</p>
          </section>

          <section className='priv-section'>
            <h2>{t.privRetentionTitle}</h2>
            <p>{t.privRetentionText}</p>
          </section>

          <section className='priv-section'>
            <h2>{t.privRightsTitle}</h2>
            <p>{t.privRightsText} <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. {t.privRightsReply}</p>
          </section>

          <section className='priv-section'>
            <h2>{t.privChangesTitle}</h2>
            <p>{t.privChangesText}</p>
          </section>
        </div>
      </div>
    </Layout>
  )
}

export default Privacy
